import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { TryOnPhotoCard } from "@/features/settings/profile/components/TryOnPhotoCard";
import { TryOnPhotoDeleteDialog } from "@/features/settings/profile/components/TryOnPhotoDeleteDialog";
import { usePersonaSetup } from "../hooks/use-persona-setup";
import { QuestCard } from "../components/QuestCard";

export const TryOnQuest: React.FC = () => {
  const { t } = useTranslation();
  const { tryOn, completeQuest } = usePersonaSetup();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { photos, limit, isLoading, isAdding, addPhoto } = tryOn;
  const emptySlotCount = Math.max(limit - photos.length, 0);
  const ready = photos.length >= 1;

  return (
    <QuestCard>
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-bold text-text-primary dark:text-gray-100 sm:text-2xl">
            {t("personaSetup.quests.tryOn.title")}
          </h2>
          <p className="text-sm text-text-tertiary dark:text-gray-400">
            {t("personaSetup.quests.tryOn.description")}
          </p>
        </div>
        <span className="shrink-0 rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-semibold tabular-nums text-text-secondary dark:bg-gray-900/70 dark:text-gray-300">
          {photos.length}/{limit}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
        {isLoading &&
          Array.from({ length: limit }).map((_, index) => (
            <div
              key={index}
              className="aspect-[3/4] animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700"
            />
          ))}

        {!isLoading &&
          photos.map((photo) => (
            <TryOnPhotoCard key={photo.id} photo={photo} />
          ))}

        {!isLoading &&
          Array.from({ length: emptySlotCount }).map((_, index) => (
            <button
              key={`empty-${index}`}
              type="button"
              disabled={isAdding}
              aria-label={t("settings.profile.tryOn.addPhoto")}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                "flex aspect-[3/4] flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-border-secondary text-text-tertiary dark:border-gray-600 dark:text-gray-400",
                isAdding
                  ? "cursor-wait opacity-70"
                  : "hover:border-text-brand/60 hover:bg-indigo-50/40 dark:hover:border-indigo-400/60 dark:hover:bg-indigo-500/10"
              )}
            >
              {isAdding ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {index === 0 && (
                <span className="px-1 text-center text-xs font-medium">
                  {t("settings.profile.tryOn.addPhoto")}
                </span>
              )}
            </button>
          ))}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png, image/jpeg"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void addPhoto(file);
        }}
      />

      <Button
        className="mt-6 w-full sm:w-auto"
        disabled={!ready || isAdding}
        onClick={() => completeQuest("tryOn")}
      >
        {t("personaSetup.continue")}
      </Button>

      <TryOnPhotoDeleteDialog />
    </QuestCard>
  );
};
